import { dynamicPage } from '@metorial/dynamic-component';
import type { ReactNode } from 'react';
import { createSlice } from '@metorial/microfrontend';
import { Navigate } from 'react-router-dom';
import { OrganizationSettingsPage } from './pages';
import { OrganizationSettingsTeamLayout } from './pages/(team)/_layout';
import { OrganizationSettingsInvitesPage } from './pages/(team)/invite';
import { OrganizationSettingsMembersPage } from './pages/(team)/members';
import { OrganizationPageLayout } from './pages/_layout';
import { SettingsHomePage } from './pages/home';
import { OrganizationSettingsProjectsPage } from './pages/projects';

// The BlackOS settings area (cloud-spec §7):
//   /o/:org                                    Home, organization pages
//   /o/:org/project/:projectId/...             project pages
//   /o/:org/project/:projectId/instance/:id/... instance pages
// Page files live in ./pages and belong to WS-G; ask the orchestrator before
// adding routes here.

type PageComponent = (...p: any[]) => ReactNode;

let page = <T extends Record<string, any>>(load: () => Promise<T>, name: keyof T) =>
  dynamicPage<any[]>(() => load().then(m => m[name] as PageComponent));

// Organization
let NotificationsPage = page(
  () => import('./pages/notifications'),
  'OrganizationNotificationsPage'
);
let UsagePage = page(() => import('./pages/usage'), 'OrganizationUsagePage');
let TeamsPage = page(() => import('./pages/(team)/teams'), 'OrganizationTeamsPage');
let MemberPage = page(() => import('./pages/(team)/member'), 'OrganizationMemberPage');
let PoliciesPage = page(() => import('./pages/(access)/policies'), 'OrganizationPoliciesPage');
let PolicyPage = page(() => import('./pages/(access)/policy'), 'OrganizationPolicyPage');
let RolesPage = page(() => import('./pages/(access)/roles'), 'OrganizationRolesPage');
let OAuthAppsPage = page(
  () => import('./pages/(access)/oauthApps'),
  'OrganizationOAuthAppsPage'
);
let ServiceAccountsPage = page(
  () => import('./pages/(access)/serviceAccounts'),
  'OrganizationServiceAccountsPage'
);
let AuthorizedApplicationsPage = page(
  () => import('./pages/(access)/authorizedApplications'),
  'OrganizationAuthorizedApplicationsPage'
);
let CliDevicesPage = page(
  () => import('./pages/(access)/cliDevices'),
  'OrganizationCliDevicesPage'
);
let TeamPage = page(() => import('./pages/(team)/team'), 'OrganizationTeamPage');
let RolePage = page(() => import('./pages/(access)/role'), 'OrganizationRolePage');
let OAuthAppPage = page(() => import('./pages/(access)/oauthApp'), 'OrganizationOAuthAppPage');
let ServiceAccountPage = page(
  () => import('./pages/(access)/serviceAccount'),
  'OrganizationServiceAccountPage'
);
let AuthorizedApplicationPage = page(
  () => import('./pages/(access)/authorizedApplication'),
  'OrganizationAuthorizedApplicationPage'
);

// Project
let ProjectProfilePage = page(() => import('./pages/(project)/profile'), 'ProjectProfilePage');
let ProjectBrandingPage = page(
  () => import('./pages/(project)/branding'),
  'ProjectBrandingPage'
);
let ProjectRetentionPage = page(
  () => import('./pages/(project)/retention'),
  'ProjectRetentionPage'
);
let ProjectScmRepositoriesPage = page(
  () => import('./pages/(project)/scm'),
  'ProjectScmRepositoriesPage'
);
let ProjectScmConnectionsPage = page(
  () => import('./pages/(project)/scmConnections'),
  'ProjectScmConnectionsPage'
);
let ProjectScmProvidersPage = page(
  () => import('./pages/(project)/scmProviders'),
  'ProjectScmProvidersPage'
);
let ProjectAuthConfigPage = page(
  () => import('./pages/(project)/authConfig'),
  'ProjectAuthConfigPage'
);
let ProjectToolCallingPage = page(
  () => import('./pages/(project)/toolCalling'),
  'ProjectToolCallingPage'
);
let ProjectSessionlessMcpPage = page(
  () => import('./pages/(project)/sessionlessMcp'),
  'ProjectSessionlessMcpPage'
);
let ProjectDelegationConfigPage = page(
  () => import('./pages/(project)/delegationConfig'),
  'ProjectDelegationConfigPage'
);

// Instance
let AuthCredentialsPage = page(
  () => import('./pages/(instance)/authCredentials'),
  'InstanceAuthCredentialsPage'
);
let AuthCredentialPage = page(
  () => import('./pages/(instance)/authCredential'),
  'InstanceAuthCredentialPage'
);
let DelegationConfigsPage = page(
  () => import('./pages/(instance)/delegationConfigs'),
  'InstanceDelegationConfigsPage'
);
let DelegationsPage = page(
  () => import('./pages/(instance)/delegations'),
  'InstanceDelegationsPage'
);
let AlertsPage = page(() => import('./pages/(instance)/alerts'), 'InstanceAlertsPage');
let MonitorsPage = page(() => import('./pages/(instance)/monitors'), 'InstanceMonitorsPage');
let ProtoGuardPage = page(
  () => import('./pages/(instance)/protoguard'),
  'InstanceProtoGuardPage'
);
let PortalsPage = page(() => import('./pages/(instance)/portals'), 'InstancePortalsPage');
let WorkforceSettingsPage = page(
  () => import('./pages/(instance)/workforceSettings'),
  'InstanceWorkforceSettingsPage'
);
let SkillsGeneralPage = page(
  () => import('./pages/(instance)/skillsGeneral'),
  'InstanceSkillsGeneralPage'
);
let AlertPage = page(() => import('./pages/(instance)/alert'), 'InstanceAlertPage');
let MonitorPage = page(() => import('./pages/(instance)/monitor'), 'InstanceMonitorPage');
let ProtoGuardFilterPage = page(
  () => import('./pages/(instance)/protoguardFilter'),
  'InstanceProtoGuardFilterPage'
);
let DelegationConfigPage = page(
  () => import('./pages/(instance)/delegationConfig'),
  'InstanceDelegationConfigPage'
);
let DelegationPage = page(
  () => import('./pages/(instance)/delegation'),
  'InstanceDelegationPage'
);

export let organizationSlice = createSlice([
  {
    path: ':organizationId',
    element: <OrganizationPageLayout />,

    children: [
      { path: '', element: <SettingsHomePage /> },
      { path: 'general', element: <OrganizationSettingsPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'usage', element: <UsagePage /> },
      { path: 'billing', element: <Navigate to="../usage" replace /> },
      { path: 'projects', element: <OrganizationSettingsProjectsPage /> },

      {
        element: <OrganizationSettingsTeamLayout />,

        children: [
          { path: 'members', element: <OrganizationSettingsMembersPage /> },
          { path: 'invites', element: <OrganizationSettingsInvitesPage /> }
        ]
      },
      { path: 'members/:memberId', element: <MemberPage /> },
      { path: 'teams', element: <TeamsPage /> },
      { path: 'teams/:teamId', element: <TeamPage /> },

      { path: 'policies', element: <PoliciesPage /> },
      { path: 'policies/:policyId', element: <PolicyPage /> },
      { path: 'roles', element: <RolesPage /> },
      { path: 'roles/:roleId', element: <RolePage /> },
      { path: 'oauth-apps', element: <OAuthAppsPage /> },
      { path: 'oauth-apps/:oauthAppId', element: <OAuthAppPage /> },
      { path: 'service-accounts', element: <ServiceAccountsPage /> },
      { path: 'service-accounts/:serviceAccountId', element: <ServiceAccountPage /> },
      { path: 'authorized-applications', element: <AuthorizedApplicationsPage /> },
      {
        path: 'authorized-applications/:installationId',
        element: <AuthorizedApplicationPage />
      },
      { path: 'cli-devices', element: <CliDevicesPage /> },

      {
        path: 'project/:projectId',
        children: [
          { path: '', element: <ProjectProfilePage /> },
          { path: 'branding', element: <ProjectBrandingPage /> },
          { path: 'retention', element: <ProjectRetentionPage /> },
          { path: 'scm', element: <ProjectScmRepositoriesPage /> },
          { path: 'scm/connections', element: <ProjectScmConnectionsPage /> },
          { path: 'scm/providers', element: <ProjectScmProvidersPage /> },
          { path: 'auth-config', element: <ProjectAuthConfigPage /> },
          { path: 'tool-calling', element: <ProjectToolCallingPage /> },
          { path: 'sessionless-mcp', element: <ProjectSessionlessMcpPage /> },
          { path: 'delegation-config', element: <ProjectDelegationConfigPage /> },

          {
            path: 'instance/:instanceId',
            children: [
              { path: '', element: <ProjectProfilePage /> },
              { path: 'configurations/auth-credentials', element: <AuthCredentialsPage /> },
              {
                path: 'configurations/auth-credential/:providerAuthCredentialsId',
                element: <AuthCredentialPage />
              },
              { path: 'identity/delegation-configs', element: <DelegationConfigsPage /> },
              {
                path: 'identity/delegation-config/:delegationConfigId',
                element: <DelegationConfigPage />
              },
              { path: 'identity/delegations', element: <DelegationsPage /> },
              { path: 'identity/delegation/:delegationId', element: <DelegationPage /> },
              { path: 'alerts', element: <AlertsPage /> },
              { path: 'alert/:alertId', element: <AlertPage /> },
              { path: 'monitors', element: <MonitorsPage /> },
              { path: 'monitor/:monitorId', element: <MonitorPage /> },
              { path: 'protoguard', element: <ProtoGuardPage /> },
              { path: 'protoguard/filter/:filterId', element: <ProtoGuardFilterPage /> },
              { path: 'portals', element: <PortalsPage /> },
              { path: 'portals/settings', element: <WorkforceSettingsPage /> },
              { path: 'skills/general', element: <SkillsGeneralPage /> }
            ]
          }
        ]
      }
    ]
  }
]);
